import { useEffect, useState } from 'react';
import {
  Shield,
  ChevronDown,
  Loader2,
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  Save,
  RotateCcw,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { domainApi } from '@/lib/api';

interface BotAccessSettingsProps {
  domainId: string;
  isVerified: boolean;
}

type BotRule = 'allow' | 'disallow';
type Preset = 'allow-all' | 'block-training' | 'block-all' | 'custom';
type SignalValue = 'yes' | 'no';

interface Bot {
  userAgent: string;
  label: string;
  description: string;
  defaultRule: BotRule;
}

const PRESET_LABELS: Record<Preset, { label: string; description: string }> = {
  'allow-all': {
    label: 'Allow all',
    description: 'Every AI crawler may access training, search, and inference.',
  },
  'block-training': {
    label: 'Block training only',
    description: 'Allow live-search bots, block training-only crawlers (GPTBot, Google-Extended, etc.).',
  },
  'block-all': {
    label: 'Block all AI',
    description: 'Disallow every listed AI crawler. Search engines (Googlebot, Bingbot) are untouched.',
  },
  custom: {
    label: 'Custom',
    description: 'Per-bot toggles below.',
  },
};

export function BotAccessSettings({ domainId, isVerified }: BotAccessSettingsProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [bots, setBots] = useState<Bot[]>([]);
  const [signalKinds, setSignalKinds] = useState<string[]>([]);
  const [preset, setPreset] = useState<Preset>('custom');
  const [perBot, setPerBot] = useState<Record<string, BotRule>>({});
  const [contentSignals, setContentSignals] = useState<Record<string, SignalValue>>({});
  const [customRules, setCustomRules] = useState('');
  const [preview, setPreview] = useState('');
  const [publishedUrl, setPublishedUrl] = useState('');
  const [proxyVerified, setProxyVerified] = useState(false);
  const [hasSavedDirectives, setHasSavedDirectives] = useState(false);

  const load = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await domainApi.getRobotsDirectives(domainId);
      setBots(res.bots);
      setSignalKinds(res.contentSignalKinds);
      setPreview(res.preview);
      setPublishedUrl(res.publishedUrl);
      setProxyVerified(res.proxyVerified);
      if (res.directives) {
        setHasSavedDirectives(true);
        setPreset(res.directives.preset);
        setPerBot(res.directives.perBot);
        setContentSignals(res.directives.contentSignals);
        setCustomRules(res.directives.custom);
      } else {
        setHasSavedDirectives(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load bot access');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isVerified && isExpanded) {
      load();
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainId, isVerified, isExpanded]);

  const applyPreset = (next: Preset) => {
    setPreset(next);
    if (next === 'allow-all') {
      setPerBot(Object.fromEntries(bots.map((b) => [b.userAgent, 'allow' as BotRule])));
      setContentSignals({ search: 'yes', 'ai-input': 'yes', 'ai-train': 'yes' });
    } else if (next === 'block-training') {
      const trainingBots = new Set([
        'GPTBot',
        'ClaudeBot',
        'Google-Extended',
        'GoogleOther',
        'Meta-ExternalAgent',
        'Bytespider',
        'CCBot',
        'Diffbot',
        'ImagesiftBot',
        'Applebot-Extended',
        'anthropic-ai',
        'cohere-ai',
        'omgili',
      ]);
      setPerBot(
        Object.fromEntries(
          bots.map((b) => [b.userAgent, trainingBots.has(b.userAgent) ? 'disallow' : 'allow'])
        )
      );
      setContentSignals({ search: 'yes', 'ai-input': 'yes', 'ai-train': 'no' });
    } else if (next === 'block-all') {
      setPerBot(Object.fromEntries(bots.map((b) => [b.userAgent, 'disallow' as BotRule])));
      setContentSignals({ search: 'no', 'ai-input': 'no', 'ai-train': 'no' });
    }
    // 'custom' leaves the current per-bot state untouched.
  };

  const toggleBot = (userAgent: string) => {
    setPerBot((prev) => {
      const cur = prev[userAgent] ?? 'allow';
      return { ...prev, [userAgent]: cur === 'allow' ? 'disallow' : 'allow' };
    });
    setPreset('custom');
  };

  const setSignal = (kind: string, value: SignalValue) => {
    setContentSignals((prev) => ({ ...prev, [kind]: value }));
    setPreset('custom');
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await domainApi.saveRobotsDirectives(domainId, {
        preset,
        perBot,
        contentSignals,
        custom: customRules,
      });
      setPreview(res.preview);
      setHasSavedDirectives(true);
      setSuccess(
        res.proxyVerified
          ? 'Saved. Live at /robots.txt on your apex.'
          : 'Saved at your subdomain. Apex proxy not verified — add the snippet above to publish at your apex.'
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Revert to the default robots.txt? Your per-bot rules will be cleared.')) return;
    setIsResetting(true);
    setError(null);
    setSuccess(null);
    try {
      await domainApi.resetRobotsDirectives(domainId);
      await load();
      setSuccess('Reverted to default robots.txt.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reset failed');
    } finally {
      setIsResetting(false);
    }
  };

  if (!isVerified) return null;

  return (
    <div className="bg-white rounded-xl border shadow-sm">
      <button
        type="button"
        onClick={() => setIsExpanded((v) => !v)}
        className="w-full flex items-center justify-between p-4 sm:p-6 text-left"
      >
        <div className="flex items-center gap-3">
          <Shield className="w-5 h-5 text-slate-500" />
          <div>
            <h2 className="text-base sm:text-lg font-semibold">Bot access control</h2>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
              Per-AI-bot rules + Content Signals for <code className="font-mono">/robots.txt</code>.
            </p>
          </div>
        </div>
        <ChevronDown className={cn('w-5 h-5 text-slate-400 transition-transform', isExpanded && 'rotate-180')} />
      </button>

      {isExpanded && (
        <div className="border-t p-4 sm:p-6 space-y-4">
          {!proxyVerified && (
            <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                Apex proxy not verified. robots.txt at your sitemap subdomain doesn't govern your apex
                — set up the snippet in "Connect your apex domain" above to make these rules take
                effect at <code className="font-mono">{publishedUrl.replace(/^https:\/\/[^/]+/, 'your-apex.com')}</code>.
              </span>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : (
            <>
              {/* Preset selector */}
              <div>
                <label className="block text-sm font-medium mb-2">Preset</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {(['allow-all', 'block-training', 'block-all', 'custom'] as Preset[]).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => applyPreset(p)}
                      className={cn(
                        'text-left p-3 rounded-md border transition-colors',
                        preset === p
                          ? 'border-indigo-600 bg-indigo-50'
                          : 'border-slate-200 hover:border-slate-300'
                      )}
                    >
                      <div className="font-medium text-sm">{PRESET_LABELS[p].label}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{PRESET_LABELS[p].description}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Per-bot grid */}
              <div>
                <label className="block text-sm font-medium mb-2">Per-bot rules</label>
                <div className="space-y-1">
                  {bots.map((bot) => {
                    const rule = perBot[bot.userAgent] ?? bot.defaultRule;
                    return (
                      <button
                        key={bot.userAgent}
                        type="button"
                        onClick={() => toggleBot(bot.userAgent)}
                        className="w-full flex items-center justify-between gap-3 p-2.5 border border-slate-200 rounded-md hover:bg-slate-50 text-left"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="font-mono text-sm">{bot.label}</div>
                          <div className="text-xs text-slate-500 truncate">{bot.description}</div>
                        </div>
                        <span
                          className={cn(
                            'text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0',
                            rule === 'allow' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          )}
                        >
                          {rule === 'allow' ? 'Allow' : 'Disallow'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Content Signals */}
              <div>
                <label className="block text-sm font-medium mb-2">Content Signals</label>
                <div className="space-y-2">
                  {signalKinds.map((kind) => {
                    const value = contentSignals[kind];
                    return (
                      <div key={kind} className="flex items-center justify-between gap-3 p-2.5 border border-slate-200 rounded-md">
                        <div className="min-w-0">
                          <div className="font-mono text-sm">{kind}</div>
                          <div className="text-xs text-slate-500">
                            {kind === 'search' && 'Allow indexing for search results.'}
                            {kind === 'ai-input' && 'Allow as live grounding for AI answers.'}
                            {kind === 'ai-train' && 'Allow as training data for AI models.'}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          {(['yes', 'no'] as SignalValue[]).map((v) => (
                            <button
                              key={v}
                              type="button"
                              onClick={() => setSignal(kind, v)}
                              className={cn(
                                'text-xs px-2.5 py-1 rounded-md font-medium',
                                value === v
                                  ? v === 'yes'
                                    ? 'bg-green-600 text-white'
                                    : 'bg-red-600 text-white'
                                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                              )}
                            >
                              {v}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Custom appended rules */}
              <div>
                <label className="block text-sm font-medium mb-1.5">Custom appended rules (optional)</label>
                <textarea
                  value={customRules}
                  onChange={(e) => {
                    setCustomRules(e.target.value);
                    setPreset('custom');
                  }}
                  rows={3}
                  spellCheck={false}
                  placeholder="# Append any extra robots.txt rules below the catch-all block&#10;Disallow: /private/&#10;Crawl-delay: 10"
                  className="w-full px-3 py-2 border border-slate-200 rounded-md font-mono text-xs focus:outline-none focus:ring-2 focus:ring-indigo-200"
                />
              </div>

              {/* Live preview */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium">Preview</label>
                  <a
                    href={publishedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
                  >
                    Open published
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <pre className="bg-slate-900 text-slate-100 text-xs p-3 rounded-md overflow-x-auto whitespace-pre-wrap break-all max-h-64 overflow-y-auto">
                  {preview || '# (preview will appear after Save)'}
                </pre>
              </div>

              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              {success && (
                <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{success}</span>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving}
                  className="inline-flex items-center gap-1 px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
                >
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save
                </button>
                {hasSavedDirectives && (
                  <button
                    type="button"
                    onClick={handleReset}
                    disabled={isResetting}
                    className="inline-flex items-center gap-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-md text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
                  >
                    {isResetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                    Revert to default
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
